// Phones: antd Select / TreeSelect / Cascader open as bottom sheets (styled by
// each app — BO mobilePopups.less, B2C mobileSelectSheet.less). A searchable
// one used to put the caret (and the keyboard) into its own field on every
// tap, under the sheet. This runtime makes a tap open only the sheet:
//
//   • the field's search input is read-only (inputmode none) — the tap opens
//     the dropdown, no keyboard;
//   • the sheet shows a search bar at its top (::before from
//     [data-m-search]); tapping it unlocks the field and raises the keyboard,
//     and what is typed is echoed in that bar (the field itself sits under
//     the sheet / keyboard);
//   • when the select closes, the field is locked again.
//
// Not touched: AutoComplete (typing is the whole point and it has no list
// before typing) and free-text tag inputs with no options — TopFilters /
// renderInput SEARCH_TAG (id select-tag-*) or any select with the class
// `m-free-text`.
// One install per app, phones only; returns the uninstall function.

const SEARCH_INPUT =
  '.ant-select-show-search:not(.ant-select-auto-complete):not(.ant-select-disabled) .ant-select-selection-search-input';
// free-text tag inputs — typing is the input
const FREE_TEXT_INPUT = '[id^="select-tag-"], .m-free-text input';
const OPEN_DROPDOWN = '.ant-select-dropdown:not(.ant-select-dropdown-hidden)';

const SEARCH_ATTR = 'data-m-search';
const SEARCH_STATE_ATTR = 'data-m-search-state';
const LOCKED_FLAG = 'mSearchLocked';
const ARMED_FLAG = 'mSearchArmed';

interface Options {
  /** Dropdowns with this class are not sheets: their field is never locked. */
  noSheetClass: string;
}

const isLockable = (el: Element | null): el is HTMLInputElement =>
  !!el &&
  el instanceof HTMLInputElement &&
  el.matches(SEARCH_INPUT) &&
  !el.matches(FREE_TEXT_INPUT);

const lock = (input: HTMLInputElement) => {
  input.readOnly = true;
  input.setAttribute('inputmode', 'none');
  input.dataset[LOCKED_FLAG] = '1';
  delete input.dataset[ARMED_FLAG];
};

const unlock = (input: HTMLInputElement) => {
  input.readOnly = false;
  input.removeAttribute('inputmode');
  delete input.dataset[LOCKED_FLAG];
};

const isSelectOpen = (input: HTMLInputElement) =>
  !!input.closest('.ant-select-open');

// The open dropdown of a select field: by its listbox id, else — antd keeps
// focus in the field while it is open — the one visible select dropdown.
const dropdownOf = (input: HTMLInputElement): HTMLElement | null => {
  const listId =
    input.getAttribute('aria-controls') || input.getAttribute('aria-owns');
  const byId = listId
    ? document.getElementById(listId)?.closest<HTMLElement>('.ant-select-dropdown')
    : null;
  if (byId && !byId.classList.contains('ant-select-dropdown-hidden')) return byId;
  const open = Array.from(
    document.querySelectorAll<HTMLElement>(OPEN_DROPDOWN)
  ).filter((el) => el.getBoundingClientRect().height > 0);
  return open.length === 1 ? open[0] : null;
};

const placeholderOf = (input: HTMLInputElement) =>
  input
    .closest('.ant-select')
    ?.querySelector('.ant-select-selection-placeholder')
    ?.textContent?.trim() || '';

export const installSelectSheetRuntime = ({ noSheetClass }: Options) => {
  if (typeof document === 'undefined') return () => undefined;

  let frame = 0;

  const sync = () => {
    frame = 0;
    const active = document.activeElement;

    // lock every searchable field that is not being typed into right now
    document.querySelectorAll<HTMLInputElement>(SEARCH_INPUT).forEach((input) => {
      if (input.matches(FREE_TEXT_INPUT)) return;
      const armed = !!input.dataset[ARMED_FLAG];
      if (armed && input === active && isSelectOpen(input)) return;
      if (armed && input === active) {
        // closed after picking: lock and drop the keyboard
        lock(input);
        input.blur();
        return;
      }
      if (!input.dataset[LOCKED_FLAG] || !input.readOnly) lock(input);
    });

    // clear the search bar of closed dropdowns
    document.querySelectorAll(`.ant-select-dropdown[${SEARCH_ATTR}]`).forEach((el) => {
      if (el.classList.contains('ant-select-dropdown-hidden')) {
        el.removeAttribute(SEARCH_ATTR);
        el.removeAttribute(SEARCH_STATE_ATTR);
      }
    });

    if (!isLockable(active)) return;
    const dropdown = dropdownOf(active);
    if (!dropdown) return;
    if (dropdown.classList.contains(noSheetClass)) {
      // not a sheet: a plain dropdown under the field — let it be typed into
      if (active.dataset[LOCKED_FLAG]) unlock(active);
      return;
    }
    const placeholder = placeholderOf(active);
    if (active.value) {
      dropdown.setAttribute(SEARCH_ATTR, active.value);
      dropdown.setAttribute(SEARCH_STATE_ATTR, 'typed');
    } else {
      dropdown.setAttribute(SEARCH_ATTR, placeholder || '…');
      dropdown.setAttribute(
        SEARCH_STATE_ATTR,
        active.dataset[ARMED_FLAG] ? 'empty' : 'idle'
      );
    }
  };

  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(sync);
  };

  // A tap on the sheet's search bar (the dropdown's ::before, above its list):
  // unlock the field and raise the keyboard. blur + focus inside the tap is
  // what makes iOS show it for an already-focused field; rc-select cancels
  // its close-on-blur when focus returns in the same tick.
  const onClick = (event: MouseEvent) => {
    const dropdown = (event.target as HTMLElement | null)?.closest?.(
      `.ant-select-dropdown[${SEARCH_ATTR}]`
    ) as HTMLElement | null;
    if (!dropdown || event.target !== dropdown) return;
    const list = dropdown.firstElementChild as HTMLElement | null;
    if (list && event.clientY >= list.getBoundingClientRect().top) return;
    const input = document.activeElement;
    if (!isLockable(input)) return;
    event.preventDefault();
    unlock(input);
    input.dataset[ARMED_FLAG] = '1';
    input.blur();
    input.focus();
    dropdown.setAttribute(SEARCH_STATE_ATTR, input.value ? 'typed' : 'empty');
  };

  sync();
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
  });
  document.addEventListener('input', schedule, true);
  document.addEventListener('focusin', schedule, true);
  document.addEventListener('focusout', schedule, true);
  document.addEventListener('click', onClick, true);

  return () => {
    if (frame) window.cancelAnimationFrame(frame);
    observer.disconnect();
    document.removeEventListener('input', schedule, true);
    document.removeEventListener('focusin', schedule, true);
    document.removeEventListener('focusout', schedule, true);
    document.removeEventListener('click', onClick, true);
    // leave every field editable again (desktop / unmount)
    document
      .querySelectorAll<HTMLInputElement>(SEARCH_INPUT)
      .forEach((input) => {
        if (input.dataset[LOCKED_FLAG]) unlock(input);
        delete input.dataset[ARMED_FLAG];
      });
    document.querySelectorAll(`[${SEARCH_ATTR}]`).forEach((el) => {
      el.removeAttribute(SEARCH_ATTR);
      el.removeAttribute(SEARCH_STATE_ATTR);
    });
  };
};

export default installSelectSheetRuntime;
